import { Flex, Text, useDisclosure, useToast } from '@chakra-ui/react';
import {
  VariantFilesInfo,
  variantsApi,
} from '@texas/api/endpoints/variantsApi';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { clientEndpoints } from '@texas/clientEndpoints';
import {
  acceptedImageFormats,
  imageExtensions,
} from '@texas/utils/helpers/filesHelper';
import { useTranslation } from 'react-i18next';
import { FileDropzone } from '../../../../shared/dropzone/FileDropzone';
import { SetStateAction, useCallback } from 'react';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ServerError } from '@texas/types';
import { ErrorsList } from '@texas/components/shared/ErrorsList';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { FileBrowserModal } from '../../../../shared/files/FileBrowserModal';
import { ImageDropzone } from '../../../../shared/dropzone/ImageDropzone';
import { maxSize } from '@texas/components/shared/dropzone/shared';
import { CaptureSessionType } from '@texas/api/endpoints/capture/captureApi';

export function ArtworkImage({
  variantId,
  variantFilesInfo,
  setVariantFilesInfo,
  allowEdit,
}: {
  variantId: number;
  variantFilesInfo: VariantFilesInfo;
  setVariantFilesInfo: (value: SetStateAction<VariantFilesInfo | null>) => void;
  allowEdit: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { request: updateRequest } = useApiRequest(
    variantsApi.updateArtworkImage,
  );

  const {
    isOpen: isRemoveFileOpen,
    onClose: onRemoveFileClose,
    onOpen: onRemoveFileOpen,
  } = useDisclosure();

  const {
    isOpen: isFileBrowserOpen,
    onClose: onFileBrowserClose,
    onOpen: onFileBrowserOpen,
  } = useDisclosure();

  const updateImage = useCallback(
    (file: FileLink) => {
      request(
        updateRequest,
        [variantId, file.id],
        (_) => {
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
          setVariantFilesInfo({
            ...variantFilesInfo,
            artworkImageId: file.id,
            artworkImageIdentifier: file.identifier,
            artworkImageArchived: false,
          });
        },
        (error: ServerError) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            description: <ErrorsList errors={error.errors} />,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [setVariantFilesInfo, t, toast, updateRequest, variantFilesInfo, variantId],
  );

  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
  } = useFileUploads(updateImage, maxSize);

  return (
    <Flex direction="column" gap={2}>
      <Text variant="header">{t('variant.artwork')}</Text>
      {variantFilesInfo.artworkImageId &&
        variantFilesInfo.artworkImageIdentifier && (
          <ImageDropzone
            imageId={variantFilesInfo.artworkImageId}
            imageIdentifier={variantFilesInfo.artworkImageIdentifier}
            imageSrc={clientEndpoints.previewArticleArtworkImage(
              variantId,
              variantFilesInfo.artworkImageId,
            )}
            imageArchived={variantFilesInfo.artworkImageArchived}
            allowEdit={allowEdit}
            onUpload={(files) => uploadFiles(files, variantFilesInfo.folderId)}
            isUploading={isUploading}
            uploadFailed={uploadFailed}
            fileUploads={fileUploads}
            removeFileUpload={removeFileUpload}
            onRemoveFileOpen={onRemoveFileOpen}
            onFileBrowserOpen={onFileBrowserOpen}
            capturePhotoProps={{
              referenceId: variantId,
              referenceType: CaptureSessionType.ArticleVariantArtwork,
              text1: t('variant.artwork'),
              onPhotoReceived: (file) => updateImage(file),
            }}
          />
        )}

      {allowEdit && !variantFilesInfo.artworkImageId && (
        <FileDropzone
          accept={acceptedImageFormats}
          isUploading={isUploading}
          uploadFailed={uploadFailed}
          fileUploads={fileUploads}
          removeFileUpload={removeFileUpload}
          onUpload={(files) => uploadFiles(files, variantFilesInfo.folderId)}
          onFileBrowserOpen={onFileBrowserOpen}
          capturePhotoProps={{
            referenceId: variantId,
            referenceType: CaptureSessionType.ArticleVariantArtwork,
            text1: t('variant.artwork'),
            onPhotoReceived: (file) => updateImage(file),
          }}
        />
      )}
      <VerifyDialogWithRequest
        headerTitle={t('fileBrowser.removeFile')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={variantsApi.deleteArtworkImage}
        args={[variantId]}
        isOpen={isRemoveFileOpen}
        onClose={onRemoveFileClose}
        onPerformed={() =>
          setVariantFilesInfo({
            ...variantFilesInfo,
            artworkImageId: undefined,
            artworkImageIdentifier: undefined,
          })
        }
        onSuccessTitle={t('fileBrowser.fileRemoved')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
      <FileBrowserModal
        rootFolderId={variantFilesInfo.folderId}
        mode="Select"
        isOpen={isFileBrowserOpen}
        onClose={onFileBrowserClose}
        extensionFilter={imageExtensions}
        onSelect={(file: FileLink) => {
          updateImage(file);
          onFileBrowserClose();
        }}
      />
    </Flex>
  );
}
